import styled, { css } from 'styled-components';

import type { PblCaseListStatus } from './types';

export const TitleBar = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  min-width: 0;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  font-size: ${(p) => (p.isMobile ? p.theme.fontSizes.md : p.theme.fontSizes.lg)};
  font-weight: 400;

  @media (max-width: 639px) {
    margin-bottom: 16px;

    > span {
      width: 100%;
      min-width: 0;
      text-align: center;
      overflow-wrap: anywhere;
    }
  }

  strong {
    font-weight: 700;
  }
`;

export const CaseListShell = styled.div`
  width: 100%;
  min-width: 0;
`;

type CardLayoutProps = {
  $mobileCardLayout?: boolean;
  $tabletCardLayout?: boolean;
};

const cardLayoutMaxWidth = (p: CardLayoutProps) => (p.$tabletCardLayout ? 768 : 639);

export const FilterBar = styled.div<{ isMobile: boolean } & CardLayoutProps>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-width: 0;
  margin-bottom: 16px;

  ${(p) =>
    (p.$mobileCardLayout || p.$tabletCardLayout) &&
    css`
      @media (max-width: ${cardLayoutMaxWidth(p)}px) {
        gap: 8px;
      }
    `}
`;

export const FilterButton = styled.button<{ active?: boolean } & CardLayoutProps>`
  min-width: 74px;
  height: 42px;
  padding: 0 24px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => (p.active ? p.theme.colors.secondary500 : p.theme.colors.gray200)};
  color: ${(p) => p.theme.colors.light};
  font-size: ${(p) => p.theme.fontSizes.md};
  cursor: pointer;

  &:hover {
    background: ${(p) => (p.active ? p.theme.colors.secondary500 : p.theme.colors.gray300)};
  }

  ${(p) =>
    (p.$mobileCardLayout || p.$tabletCardLayout) &&
    css`
      @media (max-width: ${cardLayoutMaxWidth(p)}px) {
        padding: 0 16px;
        font-size: ${(p) => p.theme.fontSizes.base};
      }
    `}
`;

export const SearchInput = styled.input<CardLayoutProps>`
  width: min(315px, 100%);
  height: 42px;
  padding: 0 12px;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.light};
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};

  &::placeholder {
    color: ${(p) => p.theme.colors.gray300};
  }

  ${(p) =>
    (p.$mobileCardLayout || p.$tabletCardLayout) &&
    css`
      @media (max-width: ${cardLayoutMaxWidth(p)}px) {
        flex: 1 1 calc(100% - 50px);
        width: auto;
        min-width: 0;
      }
    `}
`;

export const SearchIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.secondary400};
  cursor: pointer;

  img {
    width: 22px;
    height: 22px;
  }
`;

export const ConfirmButton = styled.button`
  height: 42px;
  padding: 0 28px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.primary500};
  color: ${(p) => p.theme.colors.light};
  font-size: ${(p) => p.theme.fontSizes.md};
  font-weight: 700;
  cursor: pointer;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

export const TableCard = styled.div<CardLayoutProps & { $embedded?: boolean }>`
  width: 100%;
  min-width: 0;
  padding: 34px 52px 28px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 900px) {
    padding: 24px 18px;
  }

  ${(p) =>
    p.$embedded &&
    css`
      padding: 0;
      border-radius: 0;
      background: transparent;
    `}

  ${(p) =>
    (p.$mobileCardLayout || p.$tabletCardLayout) &&
    css`
      @media (max-width: ${cardLayoutMaxWidth(p)}px) {
        padding: 0;
        border-radius: 0;
        background: transparent;
      }
    `}
`;

export const TableScroll = styled.div<CardLayoutProps>`
  width: 100%;
  min-width: 0;
  overflow-x: auto;

  ${(p) =>
    (p.$mobileCardLayout || p.$tabletCardLayout) &&
    css`
      @media (max-width: ${cardLayoutMaxWidth(p)}px) {
        overflow: visible;
      }
    `}
`;

export const CasesTable = styled.table<CardLayoutProps & { $stripedRows?: boolean; $embedded?: boolean }>`
  width: 100%;
  min-width: ${(p) => (p.$embedded ? '760px' : '1060px')};
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    padding: 14px 16px;
    border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
    text-align: left;
    font-size: ${(p) => p.theme.fontSizes.base};
    overflow-wrap: break-word;
    vertical-align: middle;
  }

  th {
    color: ${(p) => p.theme.colors.gray300};
    font-weight: 400;
  }

  td {
    color: ${(p) => p.theme.colors.gray500};
  }

  th[data-column='caseId'],
  td[data-column='caseId'] {
    width: 165px;
    white-space: nowrap;
  }

  th[data-column='applicantName'],
  td[data-column='applicantName'] {
    width: 270px;
  }

  th[data-column='creationDate'],
  td[data-column='creationDate'] {
    width: 150px;
    white-space: nowrap;
  }

  th[data-column='status'],
  td[data-column='status'] {
    width: 220px;
    white-space: normal;
  }

  th[data-column='legalServiceNeeds'],
  td[data-column='legalServiceNeeds'],
  th[data-column='enquiryCategory'],
  td[data-column='enquiryCategory'] {
    white-space: normal;
    line-height: 1.35;
  }

  ${(p) =>
    p.$embedded &&
    css`
      th[data-column='caseId'],
      td[data-column='caseId'] {
        width: 145px;
      }

      th[data-column='applicantName'],
      td[data-column='applicantName'] {
        width: 200px;
      }

      th[data-column='creationDate'],
      td[data-column='creationDate'] {
        width: 130px;
      }

      th[data-column='status'],
      td[data-column='status'] {
        width: 190px;
      }
    `}

  tbody tr:not([aria-disabled='true']) {
    cursor: pointer;
  }

  tbody tr:not([aria-disabled='true']):hover {
    background: ${(p) => p.theme.colors.gray000};
  }

  ${(p) =>
    p.$stripedRows &&
    css`
      tbody tr:nth-child(odd) {
        background: ${(p) => p.theme.colors.gray000};
      }
    `}

  ${(p) =>
    (p.$mobileCardLayout || p.$tabletCardLayout) &&
    css`
      @media (max-width: ${cardLayoutMaxWidth(p)}px) {
        display: block;
        min-width: 0;
        table-layout: auto;

        thead {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        tbody {
          display: block;
        }

        tbody tr {
          display: block;
          padding: 18px 4px;
          border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
          background: ${(p) => p.theme.colors.light};
        }

        tbody tr:first-child {
          border-top: 1px solid ${(p) => p.theme.colors.gray100};
        }

        tbody tr:nth-child(odd) {
          background: ${(p) => p.theme.colors.gray000};
        }

        tbody tr:focus-visible {
          outline: 2px solid ${(p) => p.theme.colors.primary500};
          outline-offset: -2px;
        }

        th,
        td,
        th[data-column],
        td[data-column] {
          width: auto;
          padding: 3px 0;
          border: 0;
          white-space: normal;
          font-size: 16px;
          line-height: 1.18;
        }

        td {
          display: grid;
          grid-template-columns: 125px minmax(0, 1fr);
          align-items: start;
          gap: 8px;
        }

        td::before {
          content: attr(data-label);
          color: ${(p) => p.theme.colors.gray300};
          font-weight: 400;
        }

        td[data-column='status'] {
          align-items: center;
        }

        td[colspan] {
          display: block;
          padding: 18px 0;
          color: ${(p) => p.theme.colors.gray300};
          text-align: center;
        }

        td[colspan]::before {
          content: none;
        }
      }
    `}
`;

const statusBadgeStyle = (status: PblCaseListStatus) =>
  ({
    in_progress: css`
      background: ${(p) => p.theme.colors.orange};
    `,
    to_be_rated: css`
      background: ${(p) => p.theme.colors.primary300};
    `,
    case_closed: css`
      background: ${(p) => p.theme.colors.secondary500};
    `,
    case_cancelled: css`
      background: ${(p) => p.theme.colors.gray400};
    `,
    to_be_confirmed: css`
      background: ${(p) => p.theme.colors.primary500};
    `,
    pending: css`
      background: ${(p) => p.theme.colors.warning};
    `,
  }[status]);

export const StatusBadge = styled.span<{ status: PblCaseListStatus } & CardLayoutProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 190px;
  min-height: 32px;
  padding: 4px 10px;
  border-radius: ${(p) => p.theme.radii.base};
  color: ${(p) => p.theme.colors.light};
  font-size: ${(p) => p.theme.fontSizes.base};
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  white-space: normal;
  ${(p) => statusBadgeStyle(p.status)};

  ${(p) =>
    (p.$mobileCardLayout || p.$tabletCardLayout) &&
    css`
      @media (max-width: ${cardLayoutMaxWidth(p)}px) {
        width: 100%;
        min-height: 30px;
        padding: 3px 8px;
        font-size: 16px;
      }
    `}
`;

export const TableFooter = styled.div<{ isMobile: boolean } & CardLayoutProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;
  color: ${(p) => p.theme.colors.gray300};
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};

  ${(p) =>
    (p.$mobileCardLayout || p.$tabletCardLayout) &&
    css`
      @media (max-width: ${cardLayoutMaxWidth(p)}px) {
        gap: 10px;
        margin-top: 10px;
        font-size: 14px;
        text-align: center;
      }
    `}
`;

export const PaginationGroup = styled.div<CardLayoutProps>`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${(p) => p.theme.colors.gray500};

  ${(p) =>
    (p.$mobileCardLayout || p.$tabletCardLayout) &&
    css`
      @media (max-width: ${cardLayoutMaxWidth(p)}px) {
        flex-wrap: wrap;
        justify-content: center;
        gap: 8px;
        font-size: 14px;
      }
    `}
`;

export const PageButton = styled.button<{ active?: boolean }>`
  min-width: 34px;
  height: 30px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => (p.active ? p.theme.colors.primary300 : p.theme.colors.gray100)};
  color: ${(p) => (p.active ? p.theme.colors.light : p.theme.colors.gray500)};
  cursor: pointer;
`;

export const PageSelect = styled.select`
  height: 30px;
  border: 1px solid ${(p) => p.theme.colors.primary300};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.light};
  color: ${(p) => p.theme.colors.gray500};
`;
